import React, { useState } from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { Plus, X } from './icons';
import { colors, formes, radius, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

interface TagEditorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  // Nom de la rubrique (« Compétences »...) : la saisie et « Ajouter » le disent au lecteur d'écran.
  etiquette?: string;
  testID?: string;
}

// Liste légère de pastilles : ajout un par un, retrait par pastille (compétences, centres d'intérêt).
export function TagEditor({ value, onChange, placeholder = 'Ajouter…', etiquette, testID }: TagEditorProps) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const tag = draft.trim();
    if (!tag || value.includes(tag)) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  };

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <View testID={testID} style={styles.container}>
      {value.length > 0 ? (
        <View style={styles.chips}>
          {value.map((tag, index) => (
            <Pressable
              key={tag}
              testID={`${testID}-chip-${index}`}
              accessibilityRole="button"
              accessibilityLabel={t('a11y.removeTag', { tag })}
              onPress={() => remove(index)}
              hitSlop={{ top: 2, bottom: 2 }}
              style={styles.chip}
            >
              <Text style={styles.chipText}>{tag}</Text>
              <X size={14} color={colors.electricDeep} strokeWidth={3} />
            </Pressable>
          ))}
        </View>
      ) : null}

      {/* Pastille en tirets : on y écrit, « Ajouter » (ou Entrée) la valide. */}
      <View style={styles.addRow}>
        <TextInput
          testID={`${testID}-input`}
          style={styles.input}
          accessibilityLabel={etiquette}
          placeholder={placeholder}
          placeholderTextColor={colors.grey500}
          value={draft}
          onChangeText={setDraft}
          onSubmitEditing={add}
          returnKeyType="done"
        />
        <Pressable
          testID={`${testID}-add`}
          accessibilityRole="button"
          accessibilityLabel={etiquette ? `${t('misc.add')} - ${etiquette}` : t('misc.add')}
          onPress={add}
          style={styles.add}
        >
          <Plus size={15} color={colors.electricDeep} strokeWidth={3} />
          <Text style={styles.addText}>{t('misc.add')}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
    minHeight: formes.hauteur.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  chipText: { fontSize: typography.small.size + 1, fontWeight: '700', color: colors.electricDeep },
  addRow: { flexDirection: 'row', gap: spacing.sm, alignItems: 'center' },
  input: {
    flex: 1,
    minWidth: 0,
    minHeight: formes.cible,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.bordureClaire,
    fontSize: typography.body.size,
    color: colors.night,
  },
  add: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 0,
    minHeight: formes.cible,
    paddingHorizontal: spacing.sm,
  },
  addText: { fontSize: 14, fontWeight: '800', color: colors.electricDeep },
});
